import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import WaypointAutocomplete from "@/components/WaypointAutocomplete";
import { toast } from "sonner";
import { Calendar as CalIcon, MapPin, Mountain, Activity, Link as LinkIcon, BookmarkCheck } from "lucide-react";

const EMPTY_FORM = {
  title: "",
  description: "",
  start_date: "",
  start_time: "",
  flexibility_date_days: 0,
  flexibility_time_minutes: 0,
  ride_type: "lento",
  bike_type: "bdc",
  surface_types: ["asfaltato"],
  distance_km: "",
  elevation_m: "",
  pace_kmh: "",
  location: null,
  komoot_link: "",
  waypoints: [],
  creator_power: "",
  target_level: "tutti",
};

/**
 * Shared form for creating/editing a ride.
 * @param initialValues - optional partial form values (for edit mode)
 * @param submitLabel - button text
 * @param submittingLabel - button text while loading
 * @param onSubmit(payload) - async, throws on failure (caught here)
 * @param onCancel - called on cancel button
 * @param testIdPrefix - prefix for data-testids (e.g., "publish" or "edit")
 */
export default function RideForm({
  initialValues,
  submitLabel = "Pubblica giro",
  submittingLabel = "Pubblicazione...",
  onSubmit,
  onCancel,
  testIdPrefix = "publish",
}) {
  const [catalog, setCatalog] = useState({ ride_types: [], bike_types: [], surfaces: [], levels: [] });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const savedTours = user?.komoot_tours || [];
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(initialValues || {}) });

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, val) =>
    set(k, form[k].includes(val) ? form[k].filter((x) => x !== val) : [...form[k], val]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location) {
      toast.error("Seleziona la zona di partenza");
      return;
    }
    if (!form.start_date || !form.start_time) {
      toast.error("Imposta data e ora");
      return;
    }
    setLoading(true);
    try {
      const dt = new Date(`${form.start_date}T${form.start_time}:00`);
      const payload = {
        title: form.title,
        description: form.description,
        start_datetime: dt.toISOString(),
        flexibility_date_days: Number(form.flexibility_date_days) || 0,
        flexibility_time_minutes: Number(form.flexibility_time_minutes) || 0,
        ride_type: form.ride_type,
        bike_type: form.bike_type,
        surface_types: form.surface_types,
        distance_km: Number(form.distance_km),
        elevation_m: parseInt(String(form.elevation_m).replace(/[^\d-]/g, ""), 10) || 0,
        pace_kmh: form.pace_kmh ? Number(form.pace_kmh) : 0,
        location: {
          name: form.location.name,
          label: form.location.label || form.location.name,
          lat: form.location.lat,
          lng: form.location.lng,
          meeting_notes: (form.location.meeting_notes || "").trim(),
        },
        komoot_link: (form.komoot_link || "").trim(),
        waypoints: form.waypoints || [],
        creator_power: form.creator_power ? Number(form.creator_power) : 0,
        target_level: form.target_level || "tutti",
      };
      await onSubmit(payload);
    } finally {
      setLoading(false);
    }
  };

  const tid = (s) => `${testIdPrefix}-${s}`;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <div className="tactical-card rounded-sm p-5">
        <label className="label-tag block mb-2">Titolo</label>
        <input
          data-testid={tid("title")} required value={form.title}
          onChange={(e) => set("title", e.target.value)}
          placeholder="es. Giro del Ghisallo, ritmo medio"
          className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900"
        />
      </div>

      {/* Date / Flex */}
      <div className="tactical-card rounded-sm p-5 grid md:grid-cols-2 gap-5">
        <div>
          <label className="label-tag block mb-2"><CalIcon className="w-3 h-3 inline mr-1" /> Data</label>
          <input data-testid={tid("date")} type="date" required value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
        </div>
        <div>
          <label className="label-tag block mb-2">Ora partenza</label>
          <input data-testid={tid("time")} type="time" required value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
        </div>
        <div>
          <label className="label-tag block mb-2">Flessibilità data (giorni ±)</label>
          <input data-testid={tid("flex-days")} type="number" min="0" max="14" value={form.flexibility_date_days} onChange={(e) => set("flexibility_date_days", e.target.value)} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
        </div>
        <div>
          <label className="label-tag block mb-2">Flessibilità ora (min ±)</label>
          <input data-testid={tid("flex-min")} type="number" min="0" max="240" step="15" value={form.flexibility_time_minutes} onChange={(e) => set("flexibility_time_minutes", e.target.value)} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
        </div>
      </div>

      {/* Type + bike */}
      <div className="tactical-card rounded-sm p-5 grid md:grid-cols-2 gap-5">
        <div>
          <label className="label-tag block mb-2">Tipo di giro</label>
          <select data-testid={tid("ride-type")} value={form.ride_type} onChange={(e) => set("ride_type", e.target.value)} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900">
            {catalog.ride_types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label-tag block mb-2">Tipo bici</label>
          <select data-testid={tid("bike-type")} value={form.bike_type} onChange={(e) => set("bike_type", e.target.value)} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900">
            {catalog.bike_types.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </div>
      </div>

      {/* Level + creator power */}
      <div className="tactical-card rounded-sm p-5 grid md:grid-cols-2 gap-5">
        <div>
          <label className="label-tag block mb-2">Livello richiesto</label>
          <select data-testid={tid("target-level")} value={form.target_level} onChange={(e) => set("target_level", e.target.value)} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900">
            {(catalog.levels || []).map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
          <p className="text-xs text-gray-500 mt-2">A chi è rivolto il giro? Aiuta gli altri a capire se sono adatti.</p>
        </div>
        <div>
          <label className="label-tag block mb-2">Tuo 20-min Power (W) — opzionale</label>
          <input data-testid={tid("creator-power")} type="number" min="0" max="600" value={form.creator_power} onChange={(e) => set("creator_power", e.target.value)} placeholder="es. 250" className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
          <p className="text-xs text-gray-500 mt-2">Indica il tuo livello a chi vuole unirsi. Lascia vuoto per non mostrarlo.</p>
        </div>
      </div>

      {/* Surface */}
      <div className="tactical-card rounded-sm p-5">
        <label className="label-tag block mb-3">Tipo di fondo (multi-selezione)</label>
        <div className="flex flex-wrap gap-2">
          {catalog.surfaces.map((s) => {
            const active = form.surface_types.includes(s.id);
            return (
              <button
                key={s.id} type="button"
                data-testid={tid(`surface-${s.id}`)}
                onClick={() => toggleArr("surface_types", s.id)}
                className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wider font-bold transition-colors ${
                  active ? "bg-[#E11D48] text-white border border-[#E11D48]" : "bg-transparent text-gray-700 border border-gray-300 hover:border-gray-400"
                }`}
              >{s.label}</button>
            );
          })}
        </div>
      </div>

      {/* Metrics + location */}
      <div className="tactical-card rounded-sm p-5 grid md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="label-tag block mb-2"><MapPin className="w-3 h-3 inline mr-1" /> Zona di partenza</label>
          <LocationAutocomplete
            testId={tid("location")}
            value={form.location}
            onChange={(loc) => set("location", loc)}
            placeholder="Cerca città o paese italiano..."
            required
          />
        </div>
        <div className="md:col-span-2">
          <label className="label-tag block mb-2">Punto di ritrovo (opzionale)</label>
          <input
            data-testid={tid("meeting-notes")}
            type="text"
            maxLength={140}
            value={form.location?.meeting_notes || ""}
            onChange={(e) => set("location", { ...(form.location || {}), meeting_notes: e.target.value })}
            placeholder="es. Di fronte al municipio, parcheggio del bar Centrale..."
            className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900"
          />
          <p className="text-xs text-gray-500 mt-1">Aggiungi un dettaglio sul punto esatto di ritrovo o incolla il link della posizione.</p>
        </div>
        <div>
          <label className="label-tag block mb-2">KM</label>
          <input data-testid={tid("km")} type="number" required min="1" max="600" value={form.distance_km} onChange={(e) => set("distance_km", e.target.value)} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
        </div>
        <div>
          <label className="label-tag block mb-2"><Mountain className="w-3 h-3 inline mr-1" /> Dislivello + (m)</label>
          <input data-testid={tid("elevation")} type="number" required min="0" max="6000" step="1" inputMode="numeric" value={form.elevation_m} onChange={(e) => set("elevation_m", e.target.value.replace(/[^\d]/g, ""))} className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
        </div>
        <div className="md:col-span-2">
          <label className="label-tag block mb-2"><Activity className="w-3 h-3 inline mr-1" /> Pace km/h (opzionale)</label>
          <input data-testid={tid("pace")} type="number" min="0" max="60" step="0.5" value={form.pace_kmh} onChange={(e) => set("pace_kmh", e.target.value)} placeholder="es. 28" className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
        </div>
      </div>

      {/* Route */}
      <div className="tactical-card rounded-sm p-5 space-y-6">
        <div>
          <label className="label-tag block mb-2">Inserisci tappe manualmente <span className="text-gray-400 normal-case tracking-normal font-normal">(opzionale)</span></label>
          <WaypointAutocomplete
            testId={tid("waypoints")}
            values={form.waypoints}
            onChange={(arr) => set("waypoints", arr)}
          />
        </div>

        <div className="border-t border-gray-200 pt-5">
          <label className="label-tag block mb-2"><LinkIcon className="w-3 h-3 inline mr-1" /> Link Komoot <span className="text-gray-400 normal-case tracking-normal font-normal">(opzionale)</span></label>

          {savedTours.length > 0 && (
            <div className="mb-3 p-3 bg-[#6AA127]/5 border border-[#6AA127]/30 rounded-sm" data-testid={tid("komoot-saved-picker")}>
              <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-wider font-bold text-[#6AA127]">
                <BookmarkCheck className="w-3.5 h-3.5" /> I tuoi tour salvati
              </div>
              <div className="flex flex-wrap gap-2">
                {savedTours.map((t) => {
                  const active = form.komoot_link === t.url;
                  return (
                    <button
                      key={t.tour_id}
                      type="button"
                      data-testid={tid(`komoot-saved-${t.tour_id}`)}
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          komoot_link: t.url,
                          // Do NOT overwrite km/elevation when the user picks a saved tour.
                          // The user types their own intended values; the saved tour metadata
                          // is just a bookmark of the URL.
                        }));
                        toast.success(`Tour "${t.title}" selezionato`);
                      }}
                      className={`text-left px-3 py-2 rounded-sm border text-xs max-w-full ${
                        active
                          ? "bg-[#6AA127] text-white border-[#6AA127]"
                          : "bg-white border-gray-300 text-gray-900 hover:border-[#6AA127]"
                      }`}
                    >
                      <div className="font-bold truncate max-w-[200px]">{t.title}</div>
                      <div className={`font-mono text-[10px] ${active ? "text-white/80" : "text-gray-500"}`}>
                        {t.distance_km ? `${t.distance_km} km` : ""}{t.distance_km && t.elevation_m ? " · " : ""}{t.elevation_m ? `${t.elevation_m} m` : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-500 mt-2">Aggiungi/modifica i tour salvati dalla pagina Profilo.</p>
            </div>
          )}

          <input data-testid={tid("komoot-link")} type="url" value={form.komoot_link} onChange={(e) => set("komoot_link", e.target.value)} placeholder="https://www.komoot.com/tour/..." className="w-full bg-gray-50 border border-gray-200 focus:border-[#6AA127] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
          <p className="text-xs text-gray-500 mt-2">
            {savedTours.length === 0
              ? "Incolla l'URL pubblico del tour Komoot, oppure salvalo prima dal tuo Profilo per riutilizzarlo."
              : "Puoi anche incollare un nuovo URL al volo (non viene salvato in profilo)."}
          </p>
        </div>
      </div>

      {/* Description */}
      <div className="tactical-card rounded-sm p-5">
        <label className="label-tag block mb-2">Descrizione (opzionale)</label>
        <textarea
          data-testid={tid("description")} rows={4}
          value={form.description} onChange={(e) => set("description", e.target.value)}
          placeholder="Note utili: ritmo, soste caffè, livello richiesto..."
          className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900 resize-none"
        />
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-6 py-3 border border-gray-300 text-gray-900 rounded-sm font-bold uppercase tracking-wider text-sm" data-testid={tid("cancel")}>Annulla</button>
        <button data-testid={tid("submit-btn")} type="submit" disabled={loading} className="btn-primary px-8 py-3 rounded-sm disabled:opacity-50">
          {loading ? submittingLabel : submitLabel}
        </button>
      </div>
    </form>
  );
}
